// Taken from https://github.com/IHIutch/uswds-tailwind
// Docs: https://uswds-tailwind.com/javascript/#combobox
// @ts-nocheck
import type { Alpine, ElementWithXAttributes } from "alpinejs";

function stringContainsSubstring(label: string, substring: string): boolean {
    return label.toLowerCase().includes(substring.toLowerCase());
}

function filterResults(allOptions, filterString: string): Array<{ label: string, value: string, disabled: boolean }> {
    return [...allOptions].filter((o) => o.value !== '').map((o) => ({ label: o.textContent, value: o.value, disabled: o.disabled })).filter((o) => stringContainsSubstring(o.label, filterString));
}

function getCorrespondingOption(allOptions, liElement: HTMLElement): HTMLElement {
    return [...allOptions].filter((o) => o.label === liElement.textContent.trim()).pop()
}

export default function (Alpine: Alpine) {
    Alpine.directive('combobox', (el, directive) => {
        if (directive.value === 'input') comboboxInput(el, Alpine)
        else if (directive.value === 'desc') comboboxDesc(el, Alpine)
        else if (directive.value === 'label') comboboxLabel(el, Alpine)
        else if (directive.value === 'list') comboboxList(el, Alpine)
        else if (directive.value === 'item') comboboxListItem(el, Alpine)
        else if (directive.value === 'values') comboboxValues(el, Alpine)
        else if (directive.value === 'toggle') comboboxToggleButton(el, Alpine)
        else comboboxRoot(el, Alpine)
    })

    Alpine.magic('combobox', el => {
        const $data = Alpine.$data(el)

        return {
            get allOptions() {
                return $data.allOptions
            },
            get isLoaded() {
                return $data.isLoaded
            },
            get noResults() {
                return $data.noResults
            },
            get isOpen() {
                return $data.isOpen
            }
        }
    })
}

const comboboxRoot = (el: ElementWithXAttributes<HTMLElement>, Alpine: Alpine) => {
    Alpine.bind(el, {
        'x-id'() {
            return ['combobox-label', 'combobox-list', 'combobox-input', 'combobox-desc']
        },
        'x-init'() {
            this.isLoaded = true;
            this.$watch('isOpen', value => {
                if (value && this.selectedEl) {
                    this.$nextTick(() => {
                        this.selectedEl.scrollIntoView();
                    })
                }
                if (!value) this.activeEl = undefined
            })
        },
        'x-data'() {
            return {
                listEl: undefined as HTMLElement | undefined,
                inputEl: undefined as HTMLElement | undefined,
                labelEl: undefined as HTMLElement | undefined,
                selectedEl: undefined as HTMLElement | undefined,
                activeEl: undefined as HTMLElement | undefined,
                selectEl: undefined as HTMLElement | undefined,
                comboboxDesc: undefined,
                inputValue: '',
                isLoaded: false,
                isOpen: false,
                isDirty: false,
                allOptions: [] as Array<{ label: string, value: string, disabled: boolean }>,
                get selectedValue() {
                    return this.selectedEl ? this.selectedEl.textContent.trim() : ''
                },
                get noResults() {
                    return this.isDirty && !this.allOptions.some(o => stringContainsSubstring(o.label, this.inputValue))
                },
                setInitialValue() {
                    this.value = this.selectEl.value;
                    this.inputValue = this.selectEl.value;
                    this.select(this.selectEl.options[this.selectEl.selectedIndex], false);
                },
                select(el: HTMLElement, setFocus=true) {
                    if (el.dataset.disabled === 'true') return;
                    this.selectedEl = el
                    this.inputEl.value = el.textContent.trim();
                    this.inputValue = el.textContent.trim();
                    this.selectEl.value = getCorrespondingOption(this.selectEl.children, el).value;
                    this.selectEl.dispatchEvent(new Event('change', { bubbles: true }));
                    this.isOpen = false;
                    this.isDirty = false;
                    if(setFocus) {
                        this.$focus.focus(this.inputEl);
                    }
                    this.allOptions = filterResults(this.selectEl.children, '')
                },
                reset() {
                    this.inputEl.value = '';
                    this.inputValue = '';
                    this.isDirty = false;
                    this.selectedEl = undefined;
                },
                focusFirst() {
                    const el = this.$focus.within(this.listEl).getFirst()
                    if (el) {
                        this.activeEl = el;
                        this.$focus.focus(el)
                    }
                },
                focusLast() {
                    const el = this.$focus.within(this.listEl).getLast()
                    if (el) {
                        this.activeEl = el;
                        this.$focus.focus(el)
                    }
                },
                focusNext() {
                    const el = this.$focus.within(this.listEl).getNext()
                    if (el) {
                        this.activeEl = el;
                        this.$focus.focus(el)
                    }
                },
                focusPrev() {
                    const el = this.$focus.within(this.listEl).getPrevious()
                    if (el) {
                        this.activeEl = el;
                        this.$focus.focus(el)
                    } else {
                        this.activeEl = this.inputEl;
                        this.$focus.focus(this.inputEl)
                    }
                },
                focusSelected() {
                    this.activeEl = this.selectedEl;
                    this.$focus.focus(this.selectedEl)
                },
                validateInput() {
                    this.isOpen = false
                    // If typed value matches an option value, set it as selectedEl
                    if (this.isDirty) {
                        const found = [...this.listEl.getElementsByTagName('LI')]
                            .find((li) => li.textContent.trim() === this.inputValue && li.dataset.disabled !== 'true')
                        this.selectedEl = found ? found : this.selectedEl
                    }
                    this.isDirty = false;
                    this.inputEl.value = this.selectedValue
                }
            }
        },
        '@focusout'() {
            if (!el.contains(this.$event.relatedTarget)) {
                this.validateInput()
            }
        },
    })
}

const comboboxValues = (el: ElementWithXAttributes<HTMLElement>, Alpine: Alpine) => {
    Alpine.bind(el, {
        'x-init'() {
            el.id = '';
            el.hidden = true;
            this.allOptions = [...el.children].filter((o) => o.value !== '').map((o) => ({ label: o.textContent, value: o.value, disabled: o.disabled }));
            this.selectEl = el;
        },
        ':foo'() {
            // Only filter the visible list while the user is actively typing.
            // When not dirty (e.g. after a selection or on reopen), show all
            // options so the user can always pick a different value.
            this.allOptions = filterResults(el.children, this.isDirty ? this.inputValue : '')
        },
    })
}

const comboboxInput = (el: ElementWithXAttributes<HTMLElement>, Alpine: Alpine) => {
    Alpine.bind(el, {
        ':type'() {
            return 'text'
        },
        ':role'() {
            return 'combobox'
        },
        ':autocapitalize'() {
            return 'off'
        },
        ':autocomplete'() {
            return 'off'
        },
        ':id'() {
            return this.$id('combobox-input')
        },
        ':aria-expanded'() {
            return this.isOpen
        },
        ':aria-activedescendant'() {
            return this.activeEl ? this.activeEl.id : undefined
        },
        ':aria-controls'() {
            return this.$id('combobox-list')
        },
        ':aria-owns'() {
            return this.$id('combobox-list')
        },
        ':aria-labelledby'() {
            return this.$id('combobox-label')
        },
        ':aria-describedby'() {
            const ids = []
            if (this.comboboxDesc) ids.push(this.$id('combobox-desc'))
            // The native <select> carries aria-describedby="<error id>" when the
            // field is invalid (set server-side); surface it on the search input
            // so the error is announced, not just the help description.
            const errorIds = this.selectEl?.getAttribute('aria-describedby')
            if (errorIds) ids.push(errorIds)
            return ids.length ? ids.join(' ') : undefined
        },
        'x-init'() {
            this.inputEl = el;
            // Prefill only when a real option is selected. We can't key off
            // selectedIndex: Rails omits the placeholder <option> once a value
            // is selected, so the first real option sits at index 0 too. The
            // empty-valued placeholder is the only option with value === ''.
            if(this.selectEl.value !== '') {
                this.setInitialValue(this.selectEl);
            }
        },
        '@mousedown'() {
            this.isOpen = true;
            this.activeEl = undefined
        },
        '@input'() {
            this.inputValue = el.value;
            this.isOpen = true;
            this.isDirty = true;
            if (!el.value) {
                this.reset()
            }
        },
        '@keydown.prevent.up'() {
            this.isOpen = true;
            this.$nextTick(() => {
                this.selectedEl ? this.focusSelected() : this.focusLast()
            })
        },
        '@keydown.prevent.down'() {
            this.isOpen = true;
            this.$nextTick(() => {
                this.focusFirst()
            })
        },
        '@keydown.prevent.enter'() {
            this.isOpen = true;
            this.activeEl = undefined;
        },
    })
}


const comboboxList = (el: ElementWithXAttributes<HTMLElement>, Alpine: Alpine) => {
    Alpine.bind(el, {
        ':role'() {
            return 'listbox'
        },
        ':id'() {
            return this.$id('combobox-list')
        },
        ':tabindex'() {
            return '-1'
        },
        ':aria-labelledby'() {
            return this.$id('combobox-label')
        },
        'x-init'() {
            return this.listEl = el
        },
        'x-show'() {
            return this.isOpen
        },
        'x-anchor.bottom'() {
            return this.inputEl;
        },
        '@keydown.prevent.up'() {
            this.focusPrev()
        },
        '@keydown.prevent.down'() {
            this.focusNext()
        },
        '@keydown.prevent.home'() {
            this.focusFirst()
        },
        '@keydown.prevent.end'() {
            this.focusLast()
        },
        '@keydown.prevent.shift.tab'() {
            return false
        },
    })
}
const comboboxToggleButton = (el: ElementWithXAttributes<HTMLElement>, Alpine: Alpine) => {
    Alpine.bind(el, {
        ':type'() {
            return 'button'
        },
        // aria-label is rendered server-side (localized) on the button; don't
        // clobber it here with a hardcoded English string.
        ':tabindex'() {
            return '-1'
        },
        '@mouseup.prevent'() {
            this.isOpen = !this.isOpen;
            this.$focus.focus(this.inputEl)
        }
    })
}

const comboboxListItem = (el: ElementWithXAttributes<HTMLElement>, Alpine: Alpine) => {
    Alpine.bind(el, {
        ':role'() {
            return 'option'
        },
        ':id'() {
            return this.$id('combobox-item')
        },
        ':aria-selected'() {
            return el === this.selectedEl;
        },
        ':aria-disabled'() {
            return el.dataset.disabled === 'true';
        },
        ':data-active'() {
            return el === this.activeEl;
        },
        ':tabIndex'() {
            return el === this.activeEl ? 0 : -1;
        },
        'x-init'() {
            this.$nextTick(() => {
                this.label = el.textContent.trim()
            })
        },
        'x-data'() {
            return {
                label: ''
            }
        },
        'x-show'() {
            return this.isDirty ? stringContainsSubstring(this.$data.label, this.inputValue) : true
        },
        '@mousedown.prevent'() {
            return true
        },
        '@mouseup.prevent'() {
            if (el.dataset.disabled === 'true') return;
            this.select(el);
            this.isDirty = false;
            this.isOpen = false;
        },
        '@keydown.prevent.enter'() {
            if (el.dataset.disabled === 'true') return;
            this.select(el);
            this.isDirty = false;
            this.isOpen = false;
        },
        '@mousemove.prevent'() {
            return true
        },
        '@keydown.prevent.esc'() {
            this.isOpen = false;
            this.$focus.focus(this.inputEl)
        },
    })
}

const comboboxLabel = (el: ElementWithXAttributes<HTMLElement>, Alpine: Alpine) => {
    Alpine.bind(el, {
        ':id'() {
            return this.$id('combobox-label')
        },
        ':for'() {
            return this.$id('combobox-input')
        },
        'x-init'() {
            this.labelEl = el
        },
        '@click'() {
            this.isOpen = true
        }
    })
}

const comboboxDesc = (el: ElementWithXAttributes<HTMLElement>, Alpine: Alpine)=> {
    Alpine.bind(el, {
        ':id'() {
            return this.$id('combobox-desc')
        },
        ':for'() {
            return this.$id('combobox-input')
        },
        'x-init'() {
            this.comboboxDesc = el
        },
    })
}
